import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { zslAtom } from "../templates";
import { ZSLAgent, Agent, AgentFetchResult } from "../types";
import { extractAssetId, isTemplateAgentId } from "../utils";
import { fetchAgentAtomFamily_SUSPENDS } from "./fetchAgentAtomFamily_SUSPENDS";

/**
 * Suspended atom family that resolves to:
 *   • `{ type: "success", data }` – agent data
 *   • `{ type: "forbidden" }`     – 403
 *   • `{ type: "not_found" }`     – 404
 *   • `null`                      – any other API / network error
 *   • `undefined`                 – no agentId or template-only agent
 *
 * Callers should treat both `null` **and** `undefined` as “Agent Unavailable”.
 */
export const agentAtomFamily_SUSPENDS = atomFamily(
  (agentId: string | undefined) => {
    const baseAtom = atom(
      async (get): Promise<AgentFetchResult | null | undefined> => {
        if (!agentId) {
          return;
        }

        const isTemplateAgent = isTemplateAgentId(agentId);
        const zsl = get(zslAtom);

        // If this agentId comes from a template, try to read it directly from the ZSL
        if (zsl && isTemplateAgent) {
          const coreAgentId = extractAssetId(agentId);
          const agentTemplate = zsl.agents?.find(
            (fn: ZSLAgent) => coreAgentId === fn.id,
          );

          if (agentTemplate) {
            return { type: "success", data: agentTemplate as Agent };
          }
        }

        // If it's a template id but not present in zsl, avoid calling API which would 400
        if (isTemplateAgent) {
          return;
        }

        // Delegate API retrieval to the shared fetch atom
        return await get(fetchAgentAtomFamily_SUSPENDS(agentId));
      },
    );

    baseAtom.debugLabel = `agentAtomFamily_SUSPENDS(${agentId})`;
    return baseAtom;
  },
);
